import api from '@/lib/axios';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  return (r.data as any).data ?? (r.data as T);
}

export interface ExpenseCategory {
  id: string;
  legacyId: string | null;
  name: string;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface Expense {
  id: string;
  legacyId: string | null;
  title: string;
  description: string | null;
  amount: number;
  date: string;
  academicYearId: string | null;
  categoryId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ExpenseMonthTotal {
  month: number;
  total: number;
}

export const ExpenseCategoriesApi = {
  list: async (trashed = false): Promise<ExpenseCategory[]> =>
    unwrap(await api.get('/school/expense-categories', { params: { trashed: trashed || undefined } })),
  create: async (data: { name: string; description?: string }): Promise<ExpenseCategory> =>
    unwrap(await api.post('/school/expense-categories', data)),
  update: async (id: string, data: { name?: string; description?: string }): Promise<ExpenseCategory> =>
    unwrap(await api.patch(`/school/expense-categories/${id}`, data)),
  /** Idempotent — always trashes (soft-deletes), a no-op if already trashed. Use permanentDelete() to actually remove it. */
  remove: async (id: string): Promise<{ trashed: true }> =>
    unwrap(await api.delete(`/school/expense-categories/${id}`)),
  restore: async (id: string) => unwrap(await api.patch(`/school/expense-categories/${id}/restore`)),
  /** Requires the category to already be trashed. Blocked if any expense still uses it. */
  permanentDelete: async (id: string): Promise<{ deleted: true }> =>
    unwrap(await api.post(`/school/expense-categories/${id}/permanent-delete`)),
};

export const ExpensesApi = {
  list: async (params?: { categoryId?: string; academicYearId?: string }): Promise<Expense[]> =>
    unwrap(await api.get('/school/expenses', { params })),
  create: async (data: { title: string; description?: string; amount: number; date: string; categoryId?: string; academicYearId?: string }): Promise<Expense> =>
    unwrap(await api.post('/school/expenses', data)),
  update: async (id: string, data: Partial<{ title: string; description?: string; amount: number; date: string; categoryId?: string; academicYearId?: string }>): Promise<Expense> =>
    unwrap(await api.patch(`/school/expenses/${id}`, data)),
  remove: async (id: string) => unwrap(await api.delete(`/school/expenses/${id}`)),
  filterGraph: async (sessionYearId: string): Promise<ExpenseMonthTotal[]> =>
    unwrap(await api.get('/school/expenses/filter-graph', { params: { sessionYearId } })),
  /** MIS Report pre-fill bridge — SUM(amount) grouped by category name, for one academic year + calendar month (1-12). Only nonzero categories. */
  monthlySummary: async (academicYearId: string, month: number): Promise<Record<string, number>> =>
    unwrap(await api.get('/school/expenses/monthly-summary', { params: { academicYearId, month } })),
};
